// frontend/src/components/onboarding/persistent-links-card.tsx
// Persistent onboarding links (one per channel type). GET always returns a full
// url (lazily minted server-side; never null) plus the link's post-connect
// browser redirect URLs. Regenerate rotates the link; only the new URL is
// permanent — the old one stops working immediately. Success/Failure redirect
// URLs are saved in place (PUT) and apply to every connect through this link.
import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useForm } from '@tanstack/react-form';
import { Info, Link2, Loader2, Pencil, Target } from 'lucide-react';
import { toast } from 'sonner';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { WebhookConfigDialog } from '@/components/webhook-config-dialog';
import { qk } from '@/lib/query/keys';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { FieldError } from '@/components/form/field-error';
import { optionalHttpsUrlSchema } from '@/lib/form/schemas';
import { midEllipsisUrl } from '@/lib/mid-ellipsis';
import {
  useOrgDestinationConfig,
  usePersistentLink,
  useRegeneratePersistentLink,
  useSavePersistentRedirects,
  type PersistentChannelType,
} from './queries';
import { CopyButton } from './copy-button';
import { useFacebookEnabled, useInstagramEnabled } from '@/hooks/use-app-config';

const CHANNEL_LABELS: Record<PersistentChannelType, string> = {
  whatsapp: 'WhatsApp',
  instagram: 'Instagram',
  facebook: 'Facebook',
};

const CHANNELS: PersistentChannelType[] = ['whatsapp', 'instagram', 'facebook'];

function validateOptionalHttpsUrl(value: string): string | undefined {
  const parsed = optionalHttpsUrlSchema.safeParse(value.trim());
  return parsed.success
    ? undefined
    : (parsed.error.issues[0]?.message ?? 'Enter a valid URL');
}

/**
 * Card listing one persistent (re-displayable) onboarding link per channel.
 * Each channel is a self-contained <PersistentLinkRow/> owning its own load,
 * regenerate, and redirect-save state.
 */
export function PersistentLinksCard({ orgId }: { orgId: string }) {
  const qc = useQueryClient();
  const [destOpen, setDestOpen] = useState(false);
  const destination = useOrgDestinationConfig(orgId).data;
  const hasDestination = !!destination?.webhookUrl;
  const instagramEnabled = useInstagramEnabled();
  const facebookEnabled = useFacebookEnabled();
  // Flag off → don't even fetch that persistent link: the GET lazily MINTS
  // a link when none exists, and the backend rejects that with 403
  // INSTAGRAM_DISABLED / FACEBOOK_DISABLED while the flag is off.
  const enabled: Record<PersistentChannelType, boolean> = {
    whatsapp: true,
    instagram: instagramEnabled,
    facebook: facebookEnabled,
  };
  const channels = CHANNELS.filter((c) => enabled[c]);

  return (
    <Card className="shrink-0">
      <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">
        <CardTitle className="flex items-center gap-1 text-base">
          Persistent Onboarding Links
          <Tooltip>
            <TooltipTrigger
              render={
                <Button variant="ghost" size="icon-xs" aria-label="About persistent onboarding links">
                  <Info className="size-3.5" />
                </Button>
              }
            />
            <TooltipContent className="max-w-xs">
              Connect links, one per channel type. Share them with customers to
              onboard a WhatsApp, Instagram or Facebook channel. Each link stays valid until
              you regenerate it.
            </TooltipContent>
          </Tooltip>
        </CardTitle>
        <Button type="button" variant="outline" size="sm" onClick={() => setDestOpen(true)}>
          <Target className="size-3.5" />
          Set Global Destination
        </Button>
        {/* Same webhook editor used on channel cards; PUT /organizations/:id/destination
            speaks the shared { webhookUrl, verifyToken } contract. New onboarding links
            inherit it. Invalidate the org-destination cache prefix on save. */}
        <WebhookConfigDialog
          open={destOpen}
          onOpenChange={setDestOpen}
          mode={hasDestination ? 'edit' : 'configure'}
          submitConfig={{ endpoint: `/organizations/${orgId}/destination`, method: 'PUT' }}
          currentWebhookUrl={destination?.webhookUrl ?? undefined}
          currentVerifyToken={destination?.verifyToken ?? undefined}
          onConfigured={() => {
            void qc.invalidateQueries({ queryKey: qk.org(orgId, 'destination') });
            setDestOpen(false);
          }}
        />
      </CardHeader>
      <CardContent className="space-y-6">
        {channels.map((channelType) => (
          <PersistentLinkRow key={channelType} orgId={orgId} channelType={channelType} />
        ))}
      </CardContent>
    </Card>
  );
}

/**
 * One channel row: the standing URL (text + Copy), an Edit button that opens a
 * modal for the Success/Failure redirect URLs, and Regenerate (rotates the link;
 * old URL dies, the new one stays displayed) with a post-rotate notice. Saving
 * the redirects in the modal syncs the fetched snapshot and closes the dialog.
 */
function PersistentLinkRow({
  orgId,
  channelType,
}: {
  orgId: string;
  channelType: PersistentChannelType;
}) {
  const label = CHANNEL_LABELS[channelType];
  const [regenerated, setRegenerated] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const linkQuery = usePersistentLink(orgId, channelType);
  const regenerateLink = useRegeneratePersistentLink(orgId, channelType);
  const saveRedirects = useSavePersistentRedirects(orgId, channelType);
  const savedRedirectSnapshotRef = useRef<string | null>(null);

  const link = linkQuery.data ?? null;

  const form = useForm({
    defaultValues: {
      successRedirectUrl: '',
      failureRedirectUrl: '',
    },
    onSubmit: async ({ value }) => {
      const nextSuccess = value.successRedirectUrl.trim();
      const nextFailure = value.failureRedirectUrl.trim();
      saveRedirects.mutate(
        {
          successRedirectUrl: nextSuccess,
          failureRedirectUrl: nextFailure,
        },
        {
          onSuccess: () => {
            toast.success('Redirect URLs saved');
            setEditOpen(false);
          },
          onError: (err) => {
            toast.error(err.userMessage ?? 'Failed to save redirect URLs');
          },
        },
      );
    },
  });

  // Sync fetched redirect snapshots into TanStack Form. This mirrors the old
  // saved-value snapshot behavior: a successful save updates Query data, then
  // this reset clears the dirty Save row.
  useEffect(() => {
    if (!link) return;
    const snapshot = `${link.successRedirectUrl ?? ''}\u0000${link.failureRedirectUrl ?? ''}`;
    if (savedRedirectSnapshotRef.current === snapshot) return;
    savedRedirectSnapshotRef.current = snapshot;
    // setFieldValue, not reset(values): reset swaps defaultValues but does not
    // repaint already-mounted controlled fields (org-destination-card has the
    // same sync done the working way).
    form.setFieldValue('successRedirectUrl', link.successRedirectUrl ?? '');
    form.setFieldValue('failureRedirectUrl', link.failureRedirectUrl ?? '');
  }, [form, link]);

  // Discard-on-close: Cancel, the X, Escape, and clicking outside all revert the
  // fields to the last saved values. Only Save (form submit) persists an edit.
  function closeEdit() {
    form.setFieldValue('successRedirectUrl', link?.successRedirectUrl ?? '');
    form.setFieldValue('failureRedirectUrl', link?.failureRedirectUrl ?? '');
    setEditOpen(false);
  }

  function handleRegenerate() {
    setRegenerated(false);
    regenerateLink.mutate(undefined, {
      onSuccess: () => {
        setRegenerated(true);
      },
      onError: (err) => {
        toast.error(err.userMessage ?? 'Failed to regenerate link');
      },
    });
  }

  return (
    <div className="space-y-2">
      <p className="text-sm font-medium">{label}</p>
      {link ? (
        <>
          {/* Mobile: the url gets its own full-width line (head…tail masked,
              Copy has the full value); the buttons wrap onto the next line.
              sm+: url + buttons share one row as before. */}
          <div className="flex flex-wrap items-center gap-2">
            <span
              className="min-w-0 basis-full truncate font-mono text-xs text-muted-foreground sm:flex-1 sm:basis-auto"
              title={link.url}
            >
              {midEllipsisUrl(link.url)}
            </span>
            <CopyButton value={link.url} label={`${label} Onboarding URL`} />
            <Button
              type="button"
              variant="outline"
              size="sm"
              aria-label={`Edit ${label} Redirect URLs`}
              onClick={() => setEditOpen(true)}
            >
              <Pencil className="size-3.5" />
              Edit
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={regenerateLink.isPending}
              aria-label={`Regenerate ${label} Link`}
              onClick={handleRegenerate}
            >
              {regenerateLink.isPending && <Loader2 className="size-3.5 animate-spin" />}
              Regenerate
            </Button>
          </div>

          {regenerated && (
            <Alert className="border-blue-500/30 bg-blue-500/5">
              <Link2 className="text-blue-500" />
              <AlertDescription>
                Link rotated. The old URL stopped working; this new one stays here to copy anytime.
              </AlertDescription>
            </Alert>
          )}

          <Dialog open={editOpen} onOpenChange={(open) => (open ? setEditOpen(true) : closeEdit())}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>{label} Redirect URLs</DialogTitle>
              </DialogHeader>
              <form
                noValidate
                onSubmit={(e) => {
                  e.preventDefault();
                  void form.handleSubmit();
                }}
                className="space-y-4"
              >
                <form.Field
                  name="successRedirectUrl"
                  validators={{
                    onChange: ({ value }) => validateOptionalHttpsUrl(value),
                    onSubmit: ({ value }) => validateOptionalHttpsUrl(value),
                  }}
                >
                  {(field) => {
                    const hasError = field.state.meta.errors.length > 0;
                    return (
                      <div className="space-y-2">
                        <Label htmlFor={`${channelType}-success`}>Success Redirect URL</Label>
                        <Input
                          id={`${channelType}-success`}
                          aria-label={`${label} Success Redirect URL`}
                          value={field.state.value}
                          onChange={(e) => field.handleChange(e.target.value)}
                          onBlur={field.handleBlur}
                          placeholder="https://app.example.com/connected"
                          aria-invalid={hasError || undefined}
                          aria-describedby={
                            hasError ? `${channelType}-success-error` : undefined
                          }
                        />
                        <FieldError
                          id={`${channelType}-success-error`}
                          errors={field.state.meta.errors}
                        />
                      </div>
                    );
                  }}
                </form.Field>
                <form.Field
                  name="failureRedirectUrl"
                  validators={{
                    onChange: ({ value }) => validateOptionalHttpsUrl(value),
                    onSubmit: ({ value }) => validateOptionalHttpsUrl(value),
                  }}
                >
                  {(field) => {
                    const hasError = field.state.meta.errors.length > 0;
                    return (
                      <div className="space-y-2">
                        <Label htmlFor={`${channelType}-failure`}>Failure Redirect URL</Label>
                        <Input
                          id={`${channelType}-failure`}
                          aria-label={`${label} Failure Redirect URL`}
                          value={field.state.value}
                          onChange={(e) => field.handleChange(e.target.value)}
                          onBlur={field.handleBlur}
                          placeholder="https://app.example.com/connect-failed"
                          aria-invalid={hasError || undefined}
                          aria-describedby={
                            hasError ? `${channelType}-failure-error` : undefined
                          }
                        />
                        <FieldError
                          id={`${channelType}-failure-error`}
                          errors={field.state.meta.errors}
                        />
                        <p className="text-xs text-muted-foreground">
                          We&apos;ll send your customer here if connecting fails, with
                          status=failed and a reason code.
                        </p>
                      </div>
                    );
                  }}
                </form.Field>
                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    disabled={saveRedirects.isPending}
                    onClick={closeEdit}
                  >
                    Cancel
                  </Button>
                  <form.Subscribe
                    selector={(state) => ({
                      invalid:
                        validateOptionalHttpsUrl(state.values.successRedirectUrl) !== undefined ||
                        validateOptionalHttpsUrl(state.values.failureRedirectUrl) !== undefined,
                    })}
                  >
                    {({ invalid }) => (
                      <Button
                        type="submit"
                        disabled={saveRedirects.isPending || invalid}
                        aria-label={`Save ${label} Redirect URLs`}
                      >
                        {saveRedirects.isPending && <Loader2 className="size-3.5 animate-spin" />}
                        Save
                      </Button>
                    )}
                  </form.Subscribe>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </>
      ) : (
        <p className="text-sm text-muted-foreground">Loading...</p>
      )}
    </div>
  );
}
